// CollapsedMessageRow — a one-line preview row for an older message.
//
// Polish #12 (collapsible thread). When a thread has more than one message,
// the Command Center now collapses all earlier messages into these tight
// rows (avatar + sender name + time + 60-char snippet) so the latest
// bubble can dominate the viewport. Clicking a collapsed row expands just
// that one message to a full MessageBubble.

import { useMemo } from 'react';
import { cleanEmailBody } from '@/lib/emailClean';

export interface CollapsedMessageRowProps {
  direction: 'inbound' | 'outbound';
  senderName: string | null;
  senderInitials: string;
  body: string;
  sentAt: string;
  onExpand: () => void;
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();
  const time = d
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    .toLowerCase()
    .replace(' ', ' ');
  if (sameDay) return time;
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${date}, ${time}`;
}

function snippet(body: string, max = 60): string {
  if (!body) return '';
  const cleaned = cleanEmailBody(body).text || body;
  const flat = cleaned.replace(/\s+/g, ' ').trim();
  return flat.length > max ? flat.slice(0, max).trimEnd() + '…' : flat;
}

export function CollapsedMessageRow({
  direction,
  senderName,
  senderInitials,
  body,
  sentAt,
  onExpand,
}: CollapsedMessageRowProps) {
  const preview = useMemo(() => snippet(body), [body]);
  const name = senderName && senderName.trim().length > 0 ? senderName : 'Unknown';
  return (
    <button
      type="button"
      className={`msg-collapsed msg-collapsed--${direction}`}
      onClick={onExpand}
      title="Click to expand this message"
    >
      <span className="msg-collapsed-avatar" aria-hidden>
        {senderInitials}
      </span>
      <span className="msg-collapsed-name">{name}</span>
      <span className="msg-collapsed-snippet">{preview || '(empty message)'}</span>
      <span className="msg-collapsed-when">{formatWhen(sentAt)}</span>
    </button>
  );
}
